import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useListBrowserUtils } from '@ts-private/list-browser';
import SendIcon from '@mui/icons-material/Send';
import WorkOffIcon from '@mui/icons-material/WorkOff';
import NextWeekIcon from '@mui/icons-material/NextWeek';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import { useAuthorization, useSimpleError, useToast } from 'hooks';
import useConfirmDialog from 'features/common/hooks/useConfirmDialog';
import { usePolicyRenewalUtils } from './utils';
import { useTranslation } from 'react-i18next';
import { GENERATE_RENEWAL_OFFERS, MARK_AS_NOT_RENEWABLE, MARK_AWAITING_CLIENT_RESPONSE, RECOMPUTE_RENEWAL_QUOTATIONS } from '../mutations';
import { useConditionalSubscription, useMutation } from 'features/common/security/hooks';
import {
  GENERATE_RENEWAL_OFFERS_COMPLETED,
  MARKED_AS_NOT_RENEWABLE,
  MARKED_AWAITING_CLIENT_RESPONSE_COMPLETED,
  RECOMPUTE_RENEWAL_QUOTATIONS_COMPLETED
} from '../subscriptions';
import { renewalStore } from './PolicyRenewalListBrowserContainer';

const defaultDisabled = {
  disabledGenerateOffer: false,
  disabledRecomputeQuotations: false,
  disabledSentToClient: false
};

const PolicyRenewalActions = () => {
  const { t } = useTranslation();
  const addToast = useToast();
  const showError = useSimpleError();
  const confirm = useConfirmDialog();
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditRenewal = useAuthorization(AuthorizationCodes.hasRightToEditRenewal, documentTypeId, documentStateId);
  const { deselectItems, retrieveSelectedIds, retrieveSelectionSummary, watchSelections, watchGlobalDisabled } = usePolicyRenewalUtils();
  const { addActions } = useListBrowserUtils(renewalStore);

  const [globalDisabled, setGlobalDisabled] = useState(false);
  const [disabled, setDisabled] = useState(defaultDisabled);

  useEffect(() => {
    const selectionSubscription = watchSelections(selections => {
      const actionsSummary = retrieveSelectionSummary(selections)?.actions || defaultDisabled;
      setDisabled({ ...actionsSummary });
    });

    return () => {
      selectionSubscription.unsubscribe();
    };
  }, [retrieveSelectionSummary, watchSelections, watchGlobalDisabled]);

  // #region Mutations

  const [recomputeRenewalQuotations] = useMutation(RECOMPUTE_RENEWAL_QUOTATIONS, {
    onError: error => {
      setGlobalDisabled(false);
      showError(error);
    }
  });

  const [markAwaitingClientResponse] = useMutation(MARK_AWAITING_CLIENT_RESPONSE, {
    onError: error => {
      setGlobalDisabled(false);
      showError(error);
    }
  });

  const [markNoRenewal] = useMutation(MARK_AS_NOT_RENEWABLE, {
    onError: error => {
      setGlobalDisabled(false);
      showError(error);
    }
  });

  const [generateRenewalOffers] = useMutation(GENERATE_RENEWAL_OFFERS, {
    onError: error => {
      setGlobalDisabled(false);
      showError(error);
    }
  });

  // #endregion

  // #region Handlers

  const handleRecomputeQuotations = useCallback(
    () =>
      confirm(
        () => {
          const selectedIds = retrieveSelectedIds();
          if (selectedIds.length === 0) {
            addToast(t('Offer.PolicyRenewalList.Actions.NoPolicySelected'), 'warning', { toastId: 'recomputeNoPolicySelectedToast' });
            return;
          }

          recomputeRenewalQuotations({
            variables: {
              input: {
                documentIds: selectedIds
              }
            }
          });

          setGlobalDisabled(true);
        },
        { title: t('Offer.PolicyRenewalList.Actions.RecomputeQuotations') }
      ),
    [addToast, confirm, recomputeRenewalQuotations, retrieveSelectedIds, t]
  );

  const handleMarkAwaitingClientResponse = useCallback(
    () =>
      confirm(
        () => {
          const selectedIds = retrieveSelectedIds();
          if (selectedIds.length === 0) {
            addToast(t('Offer.PolicyRenewalList.Actions.NoPolicySelected'), 'warning', { toastId: 'markAwaitingNoPolicySelectedToast' });
            return;
          }

          markAwaitingClientResponse({
            variables: {
              input: {
                documentIds: selectedIds
              }
            }
          });

          setGlobalDisabled(true);
        },
        { title: t('Offer.PolicyRenewalList.Actions.MarkAwaitingClientResponse') }
      ),
    [addToast, confirm, markAwaitingClientResponse, retrieveSelectedIds, t]
  );

  const handleMarkNotRenewable = useCallback(
    () =>
      confirm(
        () => {
          const selectedIds = retrieveSelectedIds();
          if (selectedIds.length === 0) {
            addToast(t('Offer.PolicyRenewalList.Actions.NoPolicySelected'), 'warning', {
              toastId: 'markNotRenewableNoPolicySelectedToast'
            });
            return;
          }

          markNoRenewal({
            variables: {
              input: {
                documentIds: selectedIds
              }
            }
          });

          setGlobalDisabled(true);
        },
        { title: t('Offer.PolicyRenewalList.Actions.MarkNotRenewable') }
      ),
    [addToast, confirm, markNoRenewal, retrieveSelectedIds, t]
  );

  const handleGenerateOffer = useCallback(
    () =>
      confirm(
        () => {
          const selectedIds = retrieveSelectedIds();
          if (selectedIds.length === 0) {
            addToast(t('Offer.PolicyRenewalList.Actions.NoPolicySelected'), 'warning', { toastId: 'generatedOfferNoPolicySelectedToast' });
            return;
          }

          generateRenewalOffers({
            variables: {
              input: {
                documentIds: selectedIds
              }
            }
          });

          setGlobalDisabled(true);
        },
        { title: t('Offer.PolicyRenewalList.Actions.GenerateOffer') }
      ),
    [addToast, confirm, generateRenewalOffers, retrieveSelectedIds, t]
  );

  // #endregion

  // #region Subscriptions

  useConditionalSubscription(RECOMPUTE_RENEWAL_QUOTATIONS_COMPLETED, {
    onData: _ => {
      addToast(t('Offer.PolicyRenewalList.Actions.ComputeQuotationsCompleted'), 'success', { toastId: 'renewalQuotationsCompletedToast' });
      deselectItems();
      setGlobalDisabled(false);
    }
  });

  useConditionalSubscription(MARKED_AWAITING_CLIENT_RESPONSE_COMPLETED, {
    onData: _ => {
      addToast(t('Offer.PolicyRenewalList.Actions.Marked'), 'success', { toastId: 'markedAwaitingCompletedToast' });
      deselectItems();
      setGlobalDisabled(false);
    }
  });

  useConditionalSubscription(MARKED_AS_NOT_RENEWABLE, {
    onData: _ => {
      addToast(t('Offer.PolicyRenewalList.Actions.Marked'), 'success', { toastId: 'markedNotRenewableCompletedToast' });
      deselectItems();
      setGlobalDisabled(false);
    }
  });

  useConditionalSubscription(GENERATE_RENEWAL_OFFERS_COMPLETED, {
    onData: _ => {
      addToast(t('Offer.PolicyRenewalList.Actions.GenerateOffersCompleted'), 'success', { toastId: 'generateRenewalCompletedToast' });
      deselectItems();
      setGlobalDisabled(false);
    }
  });

  // #endregion

  useEffect(() => {
    addActions([
      {
        name: 'RecomputeQuotations',
        label: t('Offer.PolicyRenewalList.Actions.RecomputeQuotations'),
        icon: <AutorenewIcon />,
        handler: handleRecomputeQuotations,
        disabled: !hasRightToEditRenewal || disabled.disabledRecomputeQuotations || globalDisabled
      },
      {
        name: 'MarkAwaitingClientResponse',
        label: t('Offer.PolicyRenewalList.Actions.MarkAwaitingClientResponse'),
        icon: <NextWeekIcon />,
        handler: handleMarkAwaitingClientResponse,
        disabled: !hasRightToEditRenewal || disabled.disabledSentToClient || globalDisabled
      },
      {
        name: 'MarkNotRenewable',
        label: t('Offer.PolicyRenewalList.Actions.MarkNotRenewable'),
        icon: <WorkOffIcon />,
        handler: handleMarkNotRenewable,
        disabled: !hasRightToEditRenewal || globalDisabled
      },
      {
        name: 'GenerateOffer',
        label: t('Offer.PolicyRenewalList.Actions.GenerateOffer'),
        icon: <SendIcon />,
        handler: handleGenerateOffer,
        disabled: !hasRightToEditRenewal || disabled.disabledGenerateOffer || globalDisabled
      }
    ]);
  }, [
    disabled,
    globalDisabled,
    handleRecomputeQuotations,
    handleMarkAwaitingClientResponse,
    handleMarkNotRenewable,
    handleGenerateOffer,
    hasRightToEditRenewal,
    t,
    addActions
  ]);
};

export default PolicyRenewalActions;
